/**
 * Shared Sentry init parameters — single source of truth for client / server /
 * edge runtimes so DSN logic, sampling, and PII scrubbing stay in lockstep.
 *
 * Imported by:
 *   - `instrumentation-client.ts` (browser, Next 15+ convention)
 *   - `instrumentation.ts` (server + edge, via the `register()` hook)
 *
 * Behavior:
 *   - **No DSN → no SDK initialization.** Local dev and preview deploys without
 *     a Sentry project configured stay completely silent, zero overhead.
 *   - **PII scrubbing is non-negotiable.** Every event passes through
 *     `sanitizeSentryEvent` from `lib/log/sanitize.ts`. Without that, Sentry's
 *     default behavior leaks user emails, IP addresses, and request payloads
 *     to a third-party SaaS.
 *   - **Sampling** defaults to 100% errors / 10% transactions in production,
 *     100% / 100% in non-prod. Tune via `SENTRY_TRACES_SAMPLE_RATE` if traffic
 *     volume forces a downsample.
 */

import { sanitizeSentryEvent, scrubString } from "@/lib/log/sanitize";

/**
 * Sentry's `beforeSend` / `beforeBreadcrumb` signatures vary slightly across
 * runtimes (`ErrorEvent` on the browser, `Event` on the server). We type the
 * hooks loosely here and let the SDK narrow at the init call site.
 */
type AnyEvent = Record<string, unknown>;
type AnyBreadcrumb = { message?: string; data?: Record<string, unknown> } & Record<string, unknown>;

export interface SentryRuntimeConfig {
    enabled: boolean;
    dsn: string | undefined;
    environment: string;
    release: string | undefined;
    tracesSampleRate: number;
    beforeSend: (event: AnyEvent) => AnyEvent | null;
    beforeBreadcrumb: (bc: AnyBreadcrumb) => AnyBreadcrumb | null;
}

function envOrUndefined(name: string): string | undefined {
    const v = process.env[name];
    if (!v || v.trim().length === 0) return undefined;
    return v.trim();
}

export function getSentryRuntimeConfig(side: "client" | "server" | "edge"): SentryRuntimeConfig {
    const dsn =
        side === "client"
            ? envOrUndefined("NEXT_PUBLIC_SENTRY_DSN")
            : envOrUndefined("SENTRY_DSN") ?? envOrUndefined("NEXT_PUBLIC_SENTRY_DSN");

    const environment =
        envOrUndefined("SENTRY_ENVIRONMENT") ??
        envOrUndefined("VERCEL_ENV") ??
        process.env.NODE_ENV ??
        "development";

    const isProd = environment === "production";

    const tracesSampleRate = (() => {
        const raw = envOrUndefined("SENTRY_TRACES_SAMPLE_RATE");
        if (raw) {
            const n = Number.parseFloat(raw);
            if (Number.isFinite(n) && n >= 0 && n <= 1) return n;
        }
        return isProd ? 0.1 : 1.0;
    })();

    return {
        enabled: !!dsn,
        dsn,
        environment,
        release: envOrUndefined("SENTRY_RELEASE") ?? envOrUndefined("VERCEL_GIT_COMMIT_SHA"),
        tracesSampleRate,
        beforeSend: (event: AnyEvent) => sanitizeSentryEvent(event),
        beforeBreadcrumb: (bc: AnyBreadcrumb) => {
            const out = { ...bc };
            if (typeof out.message === "string") {
                out.message = scrubString(out.message);
            }
            return out;
        },
    };
}
